import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getEvolution } from '../components/Fizzbuzz';
import { api } from '../auth/api';

export interface SolvedByTier {
  mid: number;
  cracked: number;
  goated: number;
}

export type AvatarChoice = 'fizz' | 'buzz' | null;

export interface UserStats {
  username: string;
  level: number;
  xp: number;
  xpToNextLevel: number;
  coins: number;
  streak: number;
  solvedCount: number;
  solvedByTopic: Record<string, number>;
  solvedByTier: SolvedByTier;
  // Friend-feature counts; mirror friend_count / pending_in_count on user_summary.
  friendCount: number;
  pendingInCount: number;
  // Account-bound mascot pick (from user_summary.avatar). null = not chosen yet.
  avatar: AvatarChoice;
}

interface UserContextValue extends UserStats {
  xpPercent: number;
  evolution: number;
  // True once the first /api/users/me fetch has resolved (or been skipped in
  // local-dev). Lets the avatar gate avoid flashing the picker before the
  // account's stored avatar has had a chance to load.
  summaryLoaded: boolean;
  addXp: (amount: number) => void;
  addCoins: (amount: number) => void;
  refresh: () => Promise<void>;
}

/** XP threshold formula from HLD: floor(100 * L^1.7) */
export function xpThreshold(level: number): number {
  return Math.floor(100 * Math.pow(level, 1.7));
}

// Closed-form inverse of xpThreshold (L = (xp / 100)^(1/1.7)). Math.floor of a
// fractional level on either side of an integer boundary occasionally rounds
// off-by-one because of float drift, so we verify against the threshold table
// and bump if needed. O(1) in the common case, O(1 worst).
function levelFromXp(xp: number): number {
  if (xp < 100) return 1;
  const approx = Math.max(1, Math.floor(Math.pow(xp / 100, 1 / 1.7)));
  let level = Math.min(67, approx);
  while (level < 67 && xp >= xpThreshold(level + 1)) level++;
  while (level > 1 && xp < xpThreshold(level)) level--;
  return level;
}

const UserContext = createContext<UserContextValue | null>(null);

const INITIAL_STATS: Omit<UserStats, 'username'> = {
  level: 1,
  xp: 0,
  xpToNextLevel: xpThreshold(2),
  coins: 0,
  streak: 0,
  solvedCount: 0,
  solvedByTopic: {},
  solvedByTier: { mid: 0, cracked: 0, goated: 0 },
  friendCount: 0,
  pendingInCount: 0,
  avatar: null,
};

export const UserProvider: React.FC<{
  username: string;
  idToken?: string;
  children: React.ReactNode;
}> = ({ username, idToken, children }) => {
  const [stats, setStats] = useState<UserStats>({ username, ...INITIAL_STATS });
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  // Cancel any in-flight `/api/users/me` when a new refresh starts. Without
  // this, a slow response from a stale token can clobber the fresh one and
  // the UI flickers back to old numbers.
  const inflightRef = React.useRef<AbortController | null>(null);

  const applySummary = useCallback((summary: Record<string, unknown>) => {
    const xp = Number(summary.xp || 0);
    const level = Math.min(67, levelFromXp(xp));
    const rawTopic = (summary.solved_by_topic ?? {}) as Record<string, unknown>;
    const solvedByTopic: Record<string, number> = {};
    for (const [k, v] of Object.entries(rawTopic)) {
      solvedByTopic[k] = Number(v) || 0;
    }
    setStats({
      username: (summary.handle as string) || username,
      level,
      xp,
      xpToNextLevel: xpThreshold(level + 1),
      coins: Number(summary.coins || 0),
      streak: Number(summary.solve_streak_current || 0),
      solvedCount: Number(summary.total_solved || 0),
      solvedByTopic,
      solvedByTier: {
        mid: Number(summary.solved_mid || 0),
        cracked: Number(summary.solved_cracked || 0),
        goated: Number(summary.solved_goated || 0),
      },
      friendCount: Number(summary.friend_count || 0),
      pendingInCount: Number(summary.pending_in_count || 0),
      avatar: summary.avatar === 'fizz' || summary.avatar === 'buzz' ? summary.avatar : null,
    });
  }, [username]);

  const refresh = useCallback(async () => {
    // No token (local-dev / signed-out demo) — nothing to fetch, but mark the
    // summary "loaded" so the avatar gate falls back to localStorage instead
    // of waiting forever.
    if (!idToken) { setSummaryLoaded(true); return; }
    inflightRef.current?.abort();
    const controller = new AbortController();
    inflightRef.current = controller;
    try {
      const res = await api('/api/users/me', { token: idToken, signal: controller.signal });
      if (!res.ok) return;
      const summary = await res.json();
      // If another refresh started while this one was awaiting JSON, skip.
      if (controller.signal.aborted) return;
      applySummary(summary);
    } catch {
      // AbortError is expected when a newer refresh supersedes; everything
      // else (network error returned by api()) is already res.ok = false.
    } finally {
      if (inflightRef.current === controller) inflightRef.current = null;
      setSummaryLoaded(true);
    }
  }, [idToken, applySummary]);

  useEffect(() => {
    void refresh();
    return () => { inflightRef.current?.abort(); };
  }, [refresh]);

  const addXp = useCallback((amount: number) => {
    setStats(prev => {
      const newXp = prev.xp + amount;
      const newLevel = Math.min(67, levelFromXp(newXp));
      return { ...prev, xp: newXp, level: newLevel, xpToNextLevel: xpThreshold(newLevel + 1) };
    });
  }, []);

  const addCoins = useCallback((amount: number) => {
    setStats(prev => ({ ...prev, coins: Math.max(0, prev.coins + amount) }));
  }, []);

  const xpPercent = useMemo(() => {
    const prevThreshold = xpThreshold(stats.level);
    const range = stats.xpToNextLevel - prevThreshold;
    if (range <= 0) return 100;
    return Math.min(100, Math.max(0, Math.round(((stats.xp - prevThreshold) / range) * 100)));
  }, [stats.xp, stats.level, stats.xpToNextLevel]);

  const evolution = useMemo(() => getEvolution(stats.level), [stats.level]);

  const value = useMemo<UserContextValue>(
    () => ({ ...stats, xpPercent, evolution, summaryLoaded, addXp, addCoins, refresh }),
    [stats, xpPercent, evolution, summaryLoaded, addXp, addCoins, refresh],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within UserProvider');
  return ctx;
}
